import {
  Component,
  Suspense,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  animate,
  motion,
  MotionConfig,
  useInView,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  type Variants,
} from 'motion/react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowRight,
  Bot,
  Building2,
  CalendarCheck,
  Check,
  ClipboardList,
  Cookie,
  Copy,
  Layers,
  Map,
  PieChart,
  Share2,
  ShieldCheck,
  Sparkles,
  User,
} from 'lucide-react';
import { DemoAuthProvider } from '@/features/auth/providers/AuthProvider';
import { HeroStory, Reveal } from '@/features/landing';
import {
  assistantChatPreview,
  eventsPreview,
  floorsPreview,
  officeBuilderPreview,
  profilePreview,
  statsPreview,
  suppliesAndSuggestionsPreview,
} from '@/features/onboarding/components/previews';
import { DEMO_USER } from '@/features/onboarding/components/previews/demo-data';
import type { SlidePreview } from '@/features/onboarding/types';
import { RippleButton } from '@/shared/components/AnimateUi/RippleButton';
import { Badge } from '@/shared/components/ui/badge';
import { HighlightedText } from '@/shared/components/ui/HighlightedText';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useAppLogo } from '@/shared/config/branding';
import { cn } from '@/shared/utils/utils';

export const Route = createFileRoute('/')({
  component: Index,
});

const MCP_SERVER_URL = 'https://mcp.robinhoot.space/';
const CLIP_RIPPLE = 'relative overflow-hidden';
const HEADER_OFFSET = 72;

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const heroStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};

const heroItem: Variants = {
  hidden: { opacity: 0, transform: 'translateY(18px)' },
  show: {
    opacity: 1,
    transform: 'translateY(0px)',
    transition: { duration: 0.6, ease: EASE_OUT },
  },
};

const heroFade: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.6, ease: EASE_OUT } },
};

const EMPTY_BOOKING_SEARCH = {
  resourceId: undefined,
  officeId: undefined,
  date: undefined,
  startTime: undefined,
  endTime: undefined,
} as const;

interface ShowcaseCardItem {
  id: string;
  badge: string;
  title: string;
  description: string;
  icon: LucideIcon;
  preview: SlidePreview;
  /** `wide` spans two columns of the grid on large screens and `tall` spans two rows. */
  layout?: 'wide' | 'tall';
  /** Puts a wide card's preview on the left. */
  flip?: boolean;
}

const FEATURE_SHOWCASE: ShowcaseCardItem[] = [
  {
    id: 'workspace',
    badge: 'Layout editor',
    title: 'Workspace management',
    description:
      'Lay out each office in the **office builder**: place desks, meeting rooms and walls, mark printers, kitchens and exits, and save the layout so everyone can book from the map. Turn **events**, **treats champions**, **guest bookings** and **facilities emails** on or off per office.',
    icon: Building2,
    preview: officeBuilderPreview,
    layout: 'wide',
  },
  {
    id: 'stats',
    badge: 'Utilization insights',
    title: 'Explore office stats',
    description:
      'See how each office is used: its **busiest days**, **occupancy rate** and **top bookers**, plus **treats champions** and the most used **meeting rooms**. Pick any **date range**, with or without weekends.',
    icon: PieChart,
    preview: statsPreview,
    layout: 'tall',
  },
  {
    id: 'floors',
    badge: 'Multi-floor maps',
    title: 'More than one floor',
    description:
      "An office can have several **floors**, and each one has its own **map**, **desks** and **meeting rooms**. Switch floors to see who's in and book a spot on the one you're visiting.",
    icon: Layers,
    preview: floorsPreview,
  },
  {
    id: 'events',
    badge: 'Office culture',
    title: 'Events and treats',
    description:
      "Spot **office events** on the calendar, share direct booking links for the day, and volunteer as **treats champion** to bring treats for everyone who's in.",
    icon: Cookie,
    preview: eventsPreview,
  },
  {
    id: 'supplies-suggestions',
    badge: 'Shared office care',
    title: 'Supplies and suggestions',
    description:
      'Ask for **water** in one tap or request any item with a **store link** on the shared shopping list, ready to copy into Slack. Send a **suggestion** to admins and office managers.',
    icon: ClipboardList,
    preview: suppliesAndSuggestionsPreview,
    layout: 'wide',
    flip: true,
  },
  {
    id: 'profile',
    badge: 'Personal preferences',
    title: 'Set up your profile',
    description:
      "Pick which **notifications** you get, turn on **Remote Worker** to email facilities when you're visiting, and add your **dietary needs** and **favourite treats**.",
    icon: User,
    preview: profilePreview,
  },
];

const PLATFORM_PILLARS: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: 'Google Workspace SSO & roles',
    description:
      'Sign in with your **Google Workspace** account, with dedicated **Admin** and **Office Manager** permissions for each office.',
    icon: ShieldCheck,
  },
  {
    title: 'Shareable booking links',
    description:
      'Generate direct links to any **office**, **floor**, **desk**, **meeting room**, or **event date** and share them in one click.',
    icon: Share2,
  },
  {
    title: 'Installable PWA & reminders',
    description:
      'Install RobinHoot on desktop or mobile with **push notifications** for booking reminders and **consecutive-day** facilities emails.',
    icon: Sparkles,
  },
];

class PreviewBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function PreviewFallback({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <div className="flex size-full items-center justify-center bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">
        <Icon className="h-8 w-8" />
      </div>
    </div>
  );
}

function LivePreviewFrame({
  preview,
  icon,
  className,
}: {
  preview: SlidePreview;
  icon: LucideIcon;
  className?: string;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const nearViewport = useInView(frameRef, { once: true, margin: '300px 0px' });

  return (
    <div
      ref={frameRef}
      inert
      className={cn(
        'h-44 sm:h-56 w-full overflow-hidden rounded-xl border border-border/80 bg-background',
        className
      )}
    >
      {nearViewport ? (
        <PreviewBoundary fallback={<PreviewFallback icon={icon} />}>
          <Suspense fallback={<Skeleton className="size-full rounded-none" />}>
            <preview.Component />
          </Suspense>
        </PreviewBoundary>
      ) : (
        <Skeleton className="size-full rounded-none" />
      )}
    </div>
  );
}

function FeatureShowcaseCard({ item, index }: { item: ShowcaseCardItem; index: number }) {
  const Icon = item.icon;
  const wide = item.layout === 'wide';
  const tall = item.layout === 'tall';
  return (
    <motion.article
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      whileInView={{
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.6, ease: EASE_OUT, delay: (index % 3) * 0.08 },
      }}
      whileHover={{ y: -4 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
      className={cn(
        'flex flex-col gap-5 rounded-3xl border p-5 sm:p-6 shadow-sm transition-shadow duration-300 hover:shadow-xl',
        wide &&
          'lg:col-span-2 lg:items-center lg:gap-8 border-primary/10 from-mint-500 via-mint-500/40 to-card hover:shadow-primary/10',
        wide &&
          (item.flip ? 'lg:flex-row-reverse bg-gradient-to-bl' : 'lg:flex-row bg-gradient-to-br'),
        !wide && 'justify-between border-border/70 hover:shadow-primary/5',
        tall ? 'lg:row-span-2 bg-gradient-to-b from-card to-mint-500/30' : !wide && 'bg-card'
      )}
    >
      <div className={cn('space-y-3', wide && 'lg:w-2/5 lg:shrink-0')}>
        <Badge variant="secondary" className="font-medium bg-primary/10 text-primary">
          <Icon className="h-3.5 w-3.5" />
          {item.badge}
        </Badge>
        <h3
          className={cn(
            'font-serif font-semibold tracking-tight text-gray-900',
            wide ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'
          )}
        >
          {item.title}
        </h3>
        <p className="text-sm sm:text-base leading-relaxed max-w-[65ch] text-gray-600">
          <HighlightedText text={item.description} />
        </p>
      </div>

      <LivePreviewFrame
        preview={item.preview}
        icon={Icon}
        className={cn(
          wide && 'sm:h-64 lg:h-72 lg:min-w-0 lg:flex-1 shadow-md',
          tall && 'lg:h-auto lg:min-h-64 lg:grow lg:basis-0'
        )}
      />
    </motion.article>
  );
}

function scrollToSection(sectionId: string, event?: MouseEvent<HTMLElement>) {
  event?.preventDefault();
  const element = document.getElementById(sectionId);
  if (!element) return;
  const start = window.scrollY;
  const top = element.getBoundingClientRect().top + start - HEADER_OFFSET;
  const controls = animate(start, top, {
    duration: 0.65,
    ease: [0.22, 1, 0.36, 1],
    onUpdate: latest => window.scrollTo(0, latest),
  });
  const cancel = () => controls.stop();
  window.addEventListener('wheel', cancel, { once: true, passive: true });
  window.addEventListener('touchmove', cancel, { once: true, passive: true });
}

function AssistantSpotlight() {
  const [copied, setCopied] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const copyServerUrl = async () => {
    try {
      await navigator.clipboard.writeText(MCP_SERVER_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy MCP server URL:', error);
    }
  };

  return (
    <div
      ref={containerRef}
      className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center"
    >
      <Reveal from="left" className="lg:col-span-5 space-y-5 [&_strong]:text-primary-foreground">
        <Badge variant="secondary" className="bg-white/10 text-mint-500 font-medium">
          <Bot className="h-3.5 w-3.5" />
          MCP Server
        </Badge>
        <h2 className="font-serif text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">
          Book with your AI assistant
        </h2>
        <p className="text-base sm:text-lg text-primary-foreground/75 leading-relaxed max-w-[58ch]">
          <HighlightedText
            text={`Add **${MCP_SERVER_URL}** as an **MCP server** in Claude, Cursor or another AI assistant, and sign in with your Google account. Then ask it to **find a free desk**, **manage your bookings**, or **check what's on** at the office.`}
          />
        </p>

        <div className="pt-1 flex flex-wrap items-center gap-2.5">
          <code className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 font-mono text-xs sm:text-sm text-primary-foreground select-all">
            {MCP_SERVER_URL}
          </code>
          <RippleButton
            variant="secondary"
            size="sm"
            className={cn(CLIP_RIPPLE, 'h-9 px-3.5')}
            onClick={copyServerUrl}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy server URL'}
          </RippleButton>
        </div>
      </Reveal>

      <Reveal from="right" delay={0.1} className="lg:col-span-7">
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-card text-foreground shadow-2xl shadow-black/30">
          <div className="flex items-center justify-between gap-2 border-b bg-white px-4 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Bot className="h-3.5 w-3.5" />
              </div>
              <span className="text-xs font-semibold text-gray-900">AI Assistant</span>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-0.5 font-mono text-[11px] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              mcp.robinhoot.space
            </span>
          </div>
          <div inert className="h-64 sm:h-80 w-full overflow-hidden bg-background">
            {inView ? (
              <PreviewBoundary fallback={<PreviewFallback icon={Bot} />}>
                <Suspense fallback={<Skeleton className="size-full rounded-none" />}>
                  <assistantChatPreview.Component />
                </Suspense>
              </PreviewBoundary>
            ) : (
              <Skeleton className="size-full rounded-none" />
            )}
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function LandingHeader() {
  const Logo = useAppLogo();
  const { scrollY, scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 200, damping: 30, restDelta: 0.001 });
  const [scrolled, setScrolled] = useState(false);

  useMotionValueEvent(scrollY, 'change', latest => setScrolled(latest > 12));

  return (
    <motion.header
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE_OUT }}
      className={cn(
        'fixed inset-x-0 top-0 z-40 border-b transition-[background-color,border-color,box-shadow] duration-300',
        scrolled
          ? 'border-primary/10 bg-white/80 shadow-sm backdrop-blur-md'
          : 'border-transparent bg-transparent'
      )}
    >
      <div
        className={cn(
          'max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 transition-[padding] duration-300',
          scrolled ? 'py-2.5' : 'py-4 sm:py-5'
        )}
      >
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img
            src={Logo}
            alt="RobinHoot"
            className={cn(
              'w-auto transition-[height] duration-300',
              scrolled ? 'h-10' : 'h-12 sm:h-14'
            )}
          />
        </Link>
        <Link to="/booking" search={EMPTY_BOOKING_SEARCH}>
          <RippleButton size="default" className={cn(CLIP_RIPPLE, 'group font-semibold shadow-sm')}>
            Start booking
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </RippleButton>
        </Link>
      </div>
      <motion.div
        aria-hidden
        style={{ scaleX: progress }}
        className="absolute inset-x-0 bottom-0 h-0.5 origin-left bg-primary"
      />
    </motion.header>
  );
}

function Index() {
  const reduceMotion = useReducedMotion();
  const item = reduceMotion ? heroFade : heroItem;

  // Waits until the hero has animated in, so downloading the previews doesn't stall it.
  useEffect(() => {
    let idleId: number | undefined;
    const preloadAll = () => {
      for (const preview of [...FEATURE_SHOWCASE.map(item => item.preview), assistantChatPreview]) {
        preview.preload();
      }
    };
    const timeout = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(preloadAll, { timeout: 2000 });
      } else {
        preloadAll();
      }
    }, 1500);
    return () => {
      window.clearTimeout(timeout);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
    };
  }, []);

  return (
    <DemoAuthProvider user={DEMO_USER}>
      <MotionConfig reducedMotion="user">
        <div className="min-h-screen bg-background text-foreground">
          <LandingHeader />

          {/* Hero Section */}
          <section className="relative isolate overflow-hidden bg-gradient-to-b from-mint-500 via-mint-500/35 to-background border-b border-primary/10">
            <motion.div
              aria-hidden
              className="absolute -top-48 -right-40 -z-10 h-[40rem] w-[40rem] bg-[radial-gradient(closest-side,var(--color-mint-500),transparent)]"
              animate={
                reduceMotion
                  ? undefined
                  : {
                      transform: [
                        'translate(0px, 0px) scale(1)',
                        'translate(-40px, 30px) scale(1.08)',
                        'translate(0px, 0px) scale(1)',
                      ],
                    }
              }
              transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            />
            <motion.div
              aria-hidden
              className="absolute top-1/4 -left-56 -z-10 h-[36rem] w-[36rem] opacity-70 bg-[radial-gradient(closest-side,var(--color-mint-500),transparent)]"
              animate={
                reduceMotion
                  ? undefined
                  : {
                      transform: [
                        'translate(0px, 0px)',
                        'translate(50px, -24px)',
                        'translate(0px, 0px)',
                      ],
                    }
              }
              transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24">
              {/* Hero Split Content */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center pt-6 pb-14 sm:pt-10 sm:pb-20 lg:pb-24">
                <motion.div
                  variants={heroStagger}
                  initial="hidden"
                  animate="show"
                  className="lg:col-span-5 space-y-6 text-left"
                >
                  <motion.div variants={item}>
                    <Badge
                      variant="secondary"
                      className="bg-primary/10 text-primary px-3 py-1 text-xs font-semibold"
                    >
                      <CalendarCheck className="h-3.5 w-3.5" />
                      Office days, sorted.
                    </Badge>
                  </motion.div>

                  <motion.h1
                    variants={item}
                    className="font-serif text-3xl sm:text-5xl font-semibold tracking-tight text-gray-900 leading-[1.12]"
                  >
                    Book a spot, see who&apos;s coming in, and keep the good vibes going.
                  </motion.h1>

                  <motion.p
                    variants={item}
                    className="text-base sm:text-lg text-gray-700 leading-relaxed max-w-[58ch]"
                  >
                    <HighlightedText text="Pick a **desk** or **meeting room** on the live office map, choose a **time slot** or **quick duration**, invite a **guest** with their Gravatar, or book **office presence** in two clicks." />
                  </motion.p>

                  <motion.div variants={item} className="flex flex-wrap items-center gap-3 pt-1">
                    <Link to="/booking" search={EMPTY_BOOKING_SEARCH}>
                      <RippleButton
                        size="lg"
                        className={cn(
                          CLIP_RIPPLE,
                          'group h-12 px-7 text-base font-semibold shadow-md transition-shadow hover:shadow-lg hover:shadow-primary/20'
                        )}
                      >
                        Start booking
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </RippleButton>
                    </Link>
                    <RippleButton
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => scrollToSection('features')}
                      className={cn(
                        CLIP_RIPPLE,
                        'h-12 px-6 text-base font-semibold bg-white/85 hover:bg-white border-primary/20'
                      )}
                    >
                      Explore features
                    </RippleButton>
                  </motion.div>

                  <motion.div
                    variants={item}
                    className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-2.5 pt-2 text-sm text-gray-700"
                  >
                    <div className="flex items-center gap-2.5">
                      <Map className="h-4 w-4 text-primary shrink-0" />
                      <span>
                        <strong className="font-semibold text-gray-900">Live office maps</strong>{' '}
                        with colleagues&apos; avatars on their desks
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Cookie className="h-4 w-4 text-primary shrink-0" />
                      <span>
                        <strong className="font-semibold text-gray-900">
                          Events, treats &amp; supplies
                        </strong>{' '}
                        built right into the calendar
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Bot className="h-4 w-4 text-primary shrink-0" />
                      <span>
                        <strong className="font-semibold text-gray-900">Remote MCP server</strong>{' '}
                        for Claude, Cursor &amp; AI agents
                      </span>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div
                  className="lg:col-span-7"
                  initial={
                    reduceMotion
                      ? { opacity: 0 }
                      : { opacity: 0, transform: 'translateY(40px) scale(0.97)' }
                  }
                  animate={
                    reduceMotion
                      ? { opacity: 1 }
                      : { opacity: 1, transform: 'translateY(0px) scale(1)' }
                  }
                  transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.25 }}
                >
                  <HeroStory />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Office Days Showcase */}
          <section id="features" className="py-14 sm:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
              <Reveal className="max-w-2xl space-y-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                  Everyday essentials
                </span>
                <h2 className="font-serif text-2xl sm:text-4xl font-semibold tracking-tight text-gray-900">
                  Built around how hybrid teams use the office
                </h2>
                <p className="text-base text-gray-600 leading-relaxed">
                  Every view below renders the real RobinHoot components with live demo data — the
                  same ones you&apos;ll find inside the welcome tour.
                </p>
              </Reveal>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {FEATURE_SHOWCASE.map((item, index) => (
                  <FeatureShowcaseCard key={item.id} item={item} index={index} />
                ))}
              </div>
            </div>
          </section>

          {/* Platform Pillars */}
          <section className="border-y border-primary/10 bg-mint-500/60">
            <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10 px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
              {PLATFORM_PILLARS.map((pillar, index) => {
                const Icon = pillar.icon;
                return (
                  <Reveal key={pillar.title} delay={index * 0.1} className="group space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-primary/10 transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">{pillar.title}</h3>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <HighlightedText text={pillar.description} />
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </section>

          {/* AI Assistant Spotlight */}
          <section
            id="ai"
            className="relative isolate overflow-hidden bg-primary text-primary-foreground"
          >
            <div
              aria-hidden
              className="absolute inset-0 -z-10 opacity-[0.07] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:22px_22px]"
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
              <AssistantSpotlight />
            </div>
          </section>

          {/* Bottom Call to Action */}
          <section className="relative isolate overflow-hidden bg-gradient-to-b from-background via-mint-500/50 to-mint-500 py-14 sm:py-18">
            <motion.div
              aria-hidden
              className="absolute -bottom-48 left-1/2 -z-10 h-[34rem] w-[60rem] -translate-x-1/2 bg-[radial-gradient(closest-side,var(--color-mint-500),transparent)]"
              animate={
                reduceMotion ? undefined : { transform: ['scale(1)', 'scale(1.12)', 'scale(1)'] }
              }
              transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6">
              <Reveal>
                <h2 className="font-serif text-2xl sm:text-4xl font-semibold tracking-tight text-gray-900">
                  Ready for your next office day?
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="text-base sm:text-lg text-gray-700 max-w-xl mx-auto leading-relaxed">
                  Join your colleagues in RobinHoot to book a desk, reserve a meeting room, and see
                  who&apos;s coming in.
                </p>
              </Reveal>
              <Reveal delay={0.16} className="flex justify-center pt-1">
                <Link to="/booking" search={EMPTY_BOOKING_SEARCH}>
                  <RippleButton
                    size="lg"
                    className={cn(
                      CLIP_RIPPLE,
                      'group h-13 px-7 text-base font-semibold shadow-lg shadow-primary/20'
                    )}
                  >
                    Start booking
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </RippleButton>
                </Link>
              </Reveal>
            </div>
          </section>
        </div>
      </MotionConfig>
    </DemoAuthProvider>
  );
}
